/**
 * Incident lifecycle state machine — single source of truth for the frontend.
 *
 * Lifecycle:
 *   Draft        → Open (set automatically when the report is Submitted)
 *   Open         → In-Progress (manual) | Closed (manual)
 *   In-Progress  → Open (manual) | Closed (manual)
 *   Overdue      → In-Progress (manual) | Closed (manual)
 *   Closed       → terminal
 *
 * Notes:
 *   - "Overdue" is system-driven only. It must never appear as a target in
 *     any manual selector — a scheduled job flips Open/In-Progress to
 *     Overdue when due_date passes. Users can transition AWAY from Overdue
 *     but never TO it.
 *   - "Draft" is a creation-time status. It can only become Open via Submit.
 *   - Backend enforcement is intentionally a separate ticket; this module
 *     is the frontend constraint, deployed first to gather telemetry on
 *     illegal-transition attempts before the API rejects them.
 */

export const INCIDENT_STATUSES = {
  DRAFT: 'Draft',
  OPEN: 'Open',
  IN_PROGRESS: 'In-Progress',
  OVERDUE: 'Overdue',
  CLOSED: 'Closed',
};

const ALLOWED_TARGETS = {
  Draft: ['Open'],
  Open: ['In-Progress', 'Closed'],
  'In-Progress': ['Open', 'Closed'],
  Overdue: ['In-Progress', 'Closed'],
  Closed: [],
};

/**
 * Return the list of statuses a manual selector should expose for a given
 * current status. The current status is always included so the <select>
 * always has a valid `value`. "Overdue" is excluded from every target list
 * because it is system-driven only.
 */
export function allowedTransitions(currentStatus) {
  const current = currentStatus || INCIDENT_STATUSES.OPEN;
  const targets = ALLOWED_TARGETS[current] || [];
  // The current value must remain selectable so React renders the right
  // option as selected; downstream save-handler is the one that decides
  // whether changing the value triggers a transition.
  const dedup = new Set([current, ...targets]);
  // Defensive: never let a UI offer "Overdue" as a manual target.
  dedup.delete(INCIDENT_STATUSES.OVERDUE);
  if (current === INCIDENT_STATUSES.OVERDUE) dedup.add(INCIDENT_STATUSES.OVERDUE);
  return Array.from(dedup);
}
